import React, { useEffect, useState, useMemo, useCallback, lazy, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CardSection from "../../../components/ui/card/CardSection";
import FormCreator from "../../../components/finalForms/forms/FormCreator"
import SidebarModel from '../../../components/ui/sidebarModel/SidebarModel';
import Buttons from "../../../components/ui/button/Buttons"
import { decryptUrlData } from "../../../services/CryptoService";
import { useLazyGetProductImportByIdQuery, useLazyGetProductImportOutputFileByIdQuery, useAddProductDataAutoSyncMutation } from "../../../app/services/productImportAPI";

import CenterModel from "../../../components/ui/centerModal/CenterModal";
import { addAutoSyncRequest } from "../config/addAutoSyncRequest.data";
import ToastService from "../../../services/toastService/ToastService";
import "./ProductImportDetail.scss";

const ProductImportInitiate = lazy(() => import('./productImportDetail/ProductImportInitiate'));
const ProductImportFiles = lazy(() => import('./productImportDetail/ProductImportFiles'));
const ProductImportLogs = lazy(() => import('./productImportDetail/ProductImportLogs'));
const SyncRequests = lazy(() => import('./productImportDetail/SyncRequests'));


const ProductImportDetail = () => {
  const userId = sessionStorage.getItem('userId');
  const [getProductImportOutputFileById, { isLoading: isGetProductImportOutputFileByIdLoading, isSuccess: isGetProductImportOutputFileByIdSuccess, data: isGetProductImportOutputFileByIdData }] = useLazyGetProductImportOutputFileByIdQuery();

  const [addProductDataAutoSync, { isLoading: isAddProductImportLoading, isSuccess: isAddProductImportSuccess, data: isAddProductImportData }] = useAddProductDataAutoSyncMutation();


  const [isModelOpen, setIsModelOpen] = useState(false)
  const formRef = useRef()
  const [formData, setFormData] = useState(addAutoSyncRequest)

  const [isAutoSyncOpen, setIsAutoSyncOpen] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const productImportId = id ? decryptUrlData(id) : 0;

  const [productImportDetails, setProductImportDetails] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeModal, setActiveModal] = useState(null);

  const [getProductImportById, { isFetching, isSuccess, data }] = useLazyGetProductImportByIdQuery();

  /** Fetch product import details on mount */
  useEffect(() => {
    if (productImportId) getProductImportById(productImportId);
  }, [productImportId, getProductImportById]);

  useEffect(() => {
    if (productImportDetails?.productImportId) {
      getProductImportOutputFileById(productImportDetails.productImportId);
    }
  }, [productImportDetails, getProductImportOutputFileById]);


  console.log("isGetProductImportOutputFileByIdData : ", isGetProductImportOutputFileByIdData);
  console.log("data : ", data);

  useEffect(() => {
    if (!isFetching && isSuccess && data) {
      setProductImportDetails(data);
      getProductImportOutputFileById(data.productImportId);
    }
  }, [isFetching, isSuccess, data]);


  /** Handlers */
  const handleBackClick = useCallback(() => navigate('/ProductImport'), [navigate]);
  const handleCloseModal = useCallback(() => setActiveModal(null), []);

  /** Tabs Configuration */
  const tabItems = useMemo(() => [
    {
      label: "Initiate",
      content: <ProductImportInitiate productImportDetails={productImportDetails} />,
    },
    {
      label: "Product Import Files",
      content: <ProductImportFiles productImportDetails={productImportDetails} />,
    },
    {
      label: "Logs",
      isSidebar: true, // open in sidebar
    },
    {
      label: "Sync Requests",
      content: <SyncRequests productImportDetails={productImportDetails} />,
    },
  ], [productImportDetails]);

  /** Sidebar Content */
  const modalContent = useMemo(() => ({
    logs: {
      title: "Logs",
      component: <ProductImportLogs MainId={productImportId} keyTypeName="ProductImportId" />,
    },
  }), [productImportId]);


  const handleSave = () => {
    const formVal = formRef.current.getFormData();
    if (!formVal) return;

    const { tutomerImport, chemPropertiesImport, pubChemSync, productUpdate, regualtion, patternTagImport, saltImport, isomerImport } = formVal;

    // Check if at least one checkbox is selected
    const isAnyChecked = tutomerImport || chemPropertiesImport || pubChemSync || productUpdate || regualtion || patternTagImport || saltImport || isomerImport;
    if (!isAnyChecked) {
      ToastService.error("Please select at least 1 checkbox.");
      return;
    }

    // Map form values to IDs
    const syncTypes = [
      { checked: tutomerImport, id: 1 },
      { checked: chemPropertiesImport, id: 2 },
      { checked: pubChemSync, id: 3 },
      { checked: productUpdate, id: 4 },
      { checked: regualtion, id: 5 },
      { checked: patternTagImport, id: 6 },
      { checked: isomerImport, id: 7 },
      { checked: saltImport, id: 8 },
    ];

    const selectedTypes = syncTypes.filter(type => type.checked);

    const newFiles = (isGetProductImportOutputFileByIdData || []).filter(f => f.labelName === "NewDataCount");
    if (newFiles.length === 0) {
      ToastService.error("No NewDataCount file found.");
      return;
    }

    const dataList = [];
    selectedTypes.forEach(type => {
      newFiles.forEach(file => {
        dataList.push({
          name: productImportDetails?.name || "N/A",
          fileName: file.fileName,
          mode: "Manual",
          file: "",
          productDataSyncImportTypeId: type.id,
          createdBy: parseInt(userId),
          moduleType: "ProductImportOutput",
          productImportId: productImportDetails?.productImportId,
          isPubChemScheduler: false
        });
      });
    });

    const requestData = { dataList };

    console.log("Request Data:", requestData);

    addProductDataAutoSync(requestData);
  };

  useEffect(() => {
    if (isAddProductImportSuccess && isAddProductImportData) {
      ToastService.success("Auto Sync added successfully!");
      setIsAutoSyncOpen(false); // Close the center modal
    }
  }, [isAddProductImportSuccess, isAddProductImportData]);

  return (
    <>
      {/* Header Section */}
      <CardSection
        cardTitle="Product Import"
        isButtonVisible
        rightButtons={
          <div className="d-flex" style={{ gap: "0.3rem" }}>
            <Buttons
              buttonTypeClassName="dark-btn"
              onClick={handleBackClick}
              buttonText="Back"
            />
          </div>
        }
      >
        <div className="product-Import-card mb-4">
          <div className="prouduct-Import-header">
            <span><b>Request :</b> </span>
            <b>{productImportDetails?.name ?? "-"}</b>
          </div>

          <div className="row p-3">
            {/* Row 1 */}
            <div className="col-md-4 mb-2">
              <span><b>Product Import Id :</b> </span>
              {productImportDetails?.productImportId ?? "-"}
            </div>
            <div className="col-md-4 mb-2">
              <span><b>Created Date :</b> </span>
              {productImportDetails?.createdDate
                ? new Date(productImportDetails.createdDate).toLocaleString()
                : "-"}
            </div>
            <div className="col-md-4 mb-2">
              {productImportDetails?.isTutomerImport ? "✔" : "❌"} <b>Is TutomerImport ?</b>
            </div>

            {/* Row 2 */}
            <div className="col-md-4 mb-2">
              <span><b>Process Time :</b> </span>
              {productImportDetails?.processTime ?? "-"}
            </div>
            <div className="col-md-4 mb-2">
              <span><b>Status :</b> </span>
              {productImportDetails?.statusName ?? "-"}
            </div>
            <div className="col-md-4 mb-2">
              {productImportDetails?.isChemPropertiesImport ? "✔" : "❌"} <b>Is ChemPropertiesImport ?</b>
            </div>

            {/* Row 3 */}
            <div className="col-md-4 mb-2">
              {productImportDetails?.isPubChemSync ? "✔" : "❌"} <b>Is PubChemSync ?</b>
            </div>
            <div className="col-md-4 mb-2">
              {productImportDetails?.isProductUpdate ? "✔" : "❌"} <b>Is ProductUpdate ?</b>
            </div>
            <div className="col-md-4 mb-2">
              {productImportDetails?.isRegulation ? "✔" : "❌"} <b>Is Regulation ?</b>
            </div>
            <div className="col-md-4 mb-2">
              {productImportDetails?.isPatternTagImport ? "✔" : "❌"} <b>Is PatternTagImport ?</b>
            </div>
            <div className="col-md-4 mb-2">
              {productImportDetails?.isSaltImport ? "✔" : "❌"} <b>Is SaltImport ?</b>
            </div>
            <div className="col-md-4 mb-2">
              {productImportDetails?.isIsomerImport ? "✔" : "❌"} <b>Is IsomerImport ?</b>
            </div>
          </div>
        </div>
      </CardSection>

      {/* Tabs Section */}
      <div className="tabs-linkupwrapper">
        <div className="tabs-linkupwrapper d-flex justify-content-between align-items-center">
          {/* Tabs */}
          <div className="tab-linkupbuttons d-flex">
            {tabItems.map((tab, index) => (
              <button
                key={index}
                className={`tab-linkupbutton ${activeIndex === index && !tab.isSidebar ? "active" : ""}`}
                onClick={() => tab.isSidebar ? setActiveModal("logs") : setActiveIndex(index)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {Array.isArray(isGetProductImportOutputFileByIdData) && isGetProductImportOutputFileByIdData.length > 0 && (
            <Buttons
              buttonTypeClassName="dark-btn"
              onClick={() => setIsAutoSyncOpen(true)}
              buttonText="Add Auto Sync"
            />
          )}

        </div>

        <CardSection>
          {tabItems[activeIndex].content}
        </CardSection>
      </div>

      {/* Sidebar Modal */}
      <SidebarModel
        modalTitle={modalContent[activeModal]?.title}
        contentClass="content-50"
        onClose={handleCloseModal}
        isOpen={!!activeModal}
      >
        {modalContent[activeModal]?.component}
      </SidebarModel>

      <CenterModel
        isOpen={isAutoSyncOpen}
        onClose={() => setIsAutoSyncOpen(false)}
        modalTitle="Add Auto Sync"
        modalSize="w-30"
      >
        <FormCreator
          ref={formRef}
          config={formData} />

        <div className="row mt-2 mb-1">
          <div className="col-12">
            <div className="form-btn d-flex justify-content-end">
              <Buttons
                buttonTypeClassName="dark-btn"
                onClick={() => setIsAutoSyncOpen(false)}
                buttonText="Cancel"
              />
              <Buttons
                buttonTypeClassName="theme-button ml-5"
                onClick={handleSave}
                buttonText="Add"
              />
            </div>
          </div>
        </div>
      </CenterModel>

    </>
  );
};

export default ProductImportDetail;
